import { useEffect, useRef, useCallback, useState } from 'react';
import { io, Socket } from 'socket.io-client';

const WS_URL = import.meta.env.VITE_WS_URL || 'ws://localhost:3000';

interface UseWebSocketOptions {
  autoConnect?: boolean;
}

export function useWebSocket(options: UseWebSocketOptions = {}) {
  const { autoConnect = true } = options;
  const socketRef = useRef<Socket | null>(null);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    if (!autoConnect) return;

    const token = localStorage.getItem('access_token');
    if (!token) return;

    // Initialize Socket.io client with reconnection options
    const socket = io(WS_URL, {
      path: '/ws',
      auth: {
        token,
      },
      transports: ['websocket'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 10000,
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('✅ WebSocket connected');
      setConnected(true);
    });

    socket.on('disconnect', (reason) => {
      console.log('❌ WebSocket disconnected:', reason);
      setConnected(false);
    });

    socket.on('connect_error', (error: any) => {
      // Suppress connection errors in production, they're expected during reconnection
      if (process.env.NODE_ENV === 'development') {
        console.warn('WebSocket connection error (will retry):', error.message);
      }
    });

    socket.on('error', (error: any) => {
      console.error('WebSocket error:', error);
    });

    // Cleanup on unmount
    return () => {
      socket.disconnect();
    };
  }, [autoConnect]);

  const emit = useCallback((event: string, data?: any) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit(event, data);
    } else {
      console.warn('Socket not connected, cannot emit event:', event);
    }
  }, []);

  const on = useCallback((event: string, handler: (data: any) => void) => {
    socketRef.current?.on(event, handler);
    
    // Return cleanup function
    return () => {
      socketRef.current?.off(event, handler);
    };
  }, []);

  const off = useCallback((event: string, handler?: (data: any) => void) => {
    if (handler) {
      socketRef.current?.off(event, handler);
    } else {
      socketRef.current?.off(event);
    }
  }, []);

  const disconnect = useCallback(() => {
    socketRef.current?.disconnect();
  }, []);

  const connect = useCallback(() => {
    socketRef.current?.connect();
  }, []);

  return {
    socket: socketRef.current,
    connected,
    emit,
    on,
    off,
    connect,
    disconnect,
  };
}

// Hook for notification events
export function useNotifications() {
  const { on, off, connected } = useWebSocket();
  const [notifications, setNotifications] = useState<any[]>([]);

  useEffect(() => {
    if (!connected) return;

    const handleNewNotification = (notification: any) => {
      setNotifications((prev) => [notification, ...prev]);
    };

    const handleNotificationRead = (data: { notificationId: string }) => {
      setNotifications((prev) =>
        prev.map((n) => (n.id === data.notificationId ? { ...n, read: true } : n))
      );
    };

    const cleanup1 = on('notification:new', handleNewNotification);
    const cleanup2 = on('notification:read', handleNotificationRead);

    return () => {
      cleanup1();
      cleanup2();
    };
  }, [connected, on, off]);

  return { notifications };
}

// Hook for chat events
export function useChat(chatId?: string) {
  const { emit, on, off, connected } = useWebSocket();
  const [messages, setMessages] = useState<any[]>([]);

  useEffect(() => {
    if (!connected || !chatId) return;

    // Join chat room
    emit('chat:join', { chatId });

    const handleNewMessage = (message: any) => {
      if (message.chatId === chatId) {
        setMessages((prev) => [...prev, message]);
      }
    };

    const handleTyping = (data: { chatId: string; userId: string; isTyping: boolean }) => {
      // Handle typing indicator
      console.log('User typing:', data);
    };

    const cleanup1 = on('chat:message', handleNewMessage);
    const cleanup2 = on('chat:typing', handleTyping);

    return () => {
      emit('chat:leave', { chatId });
      cleanup1();
      cleanup2();
    };
  }, [connected, chatId, emit, on, off]);

  const sendMessage = useCallback(
    (content: string) => {
      if (!chatId) return;
      emit('chat:message', { chatId, content });
    },
    [chatId, emit]
  );

  const setTyping = useCallback(
    (isTyping: boolean) => {
      if (!chatId) return;
      emit('chat:typing', { chatId, isTyping });
    },
    [chatId, emit]
  );

  return { messages, sendMessage, setTyping, connected };
}

// Hook for team pulse updates
export function useTeamPulse(teamId?: string) {
  const { on, off, connected } = useWebSocket();
  const [pulseData, setPulseData] = useState<any>(null);

  useEffect(() => {
    if (!connected || !teamId) return;

    const handlePulseUpdate = (data: any) => {
      if (data.teamId === teamId) {
        setPulseData(data);
      }
    };

    const cleanup = on('team:pulse_update', handlePulseUpdate);

    return () => {
      cleanup();
    };
  }, [connected, teamId, on, off]);

  return { pulseData };
}
